import axios from "axios";
import { config } from "../config/Config";

/**
 * Map a UserRegistryDTO (from GET /v1/web/user) into the shape the
 * Redux dashboardContext expects. Only returns the preference fields —
 * caller spreads it into updateDashboardContext().
 */
export const buildPreferencesPatch = (user) => {
    const global = user?.globalPreferences || {};
    return {
        defaultStartPage: global.defaultLandingPage || 'home',
        sidebarOrder: global.sidebarOrder || {},
        sidebarPinned: global.sidebarPinned || [],
        homeWidgetsHidden: global.homeWidgetsHidden || [],
        homeWidgetsOrder: global.homeWidgetsOrder || [],
        kpiSavedViews: global.kpiSavedViews || [],
        recentItems: global.recentItems || {}
    };
};

export const getUser= async (obj) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user`,
            headers: {
                'Content-Type': 'application/json',
                ...(obj.accessToken ? { 'Authorization': `Bearer ${obj.accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching new user:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Suspend / reactivate / archive an org member by setting their org-membership state.
 * `obj` = { accessToken, targetUserId, orgId, state } where state ∈ ACTIVE | SUSPENDED | ARCHIVED.
 */
export const setOrgUserState = async (obj) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/user/org-state`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`
            },
            data: {
                targetUserId: obj.targetUserId,
                orgId: obj.orgId,
                state: obj.state,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error setting org user state:', error.response?.data || error.message);
        throw error;
    }
};

export const createNewUser = async (obj) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/user`,
            data: {
                userId: obj.userId,
                userName: obj.userName,
                orgId: obj.orgId,
                title: obj.roleId,
                selectedPreferenceIds: obj.selectedPreferenceIds
            },
            headers: {
                'Content-Type': 'application/json',
                ...(obj.accessToken ? { 'Authorization': `Bearer ${obj.accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating new user:', error.response?.data || error.message);
        throw error;
    }
};

export const getAllCompanies = async () => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/game-ecosystem/companies`,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        return response.data;
    } catch (error) {
        console.error('Error creating new user:', error.response?.data || error.message);
        throw error;
    }
};

export const getAllRoles = async (token) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/role`,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error creating new user:', error.response?.data || error.message);
        throw error;
    }
};

export const getUserPreferences = async (token) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user/user-pref-list`,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error getting preferences:', error.response?.data || error.message);
        throw error;
    }
};

export const updateActiveContext = async (obj) => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${config.serverBaseUrl}/v1/web/user/active-context`,
            data: {
                activeOrgId: obj.activeOrgId || null,
                activeGameId: obj.activeGameId || null,
                activeClientId: obj.activeClientId || null,
            },
            headers: {
                'Content-Type': 'application/json',
                ...(obj.accessToken ? { 'Authorization': `Bearer ${obj.accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating active context:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Partial update for user preferences. Any field left undefined is untouched
 * server-side (atomic $set applied only to provided fields). All fields are global.
 *
 * Accepted fields:
 *   defaultLandingPage  — string
 *   sidebarOrder        — { [orderKey]: [itemId, ...] }
 *   sidebarPinned       — [path, ...]
 *   homeWidgetsHidden   — [widgetId, ...]
 *   homeWidgetsOrder    — [widgetId, ...]
 *   kpiSavedViews       — [{ name, segment, dateRange, metric, startDate, endDate, visible }, ...]
 *   recentItems         — { [orgId_gameId_clientId]: [...entries] }
 */
export const updatePreferences = async (obj = {}) => {
    const payload = {};
    if (obj.defaultLandingPage !== undefined) payload.defaultLandingPage = obj.defaultLandingPage;
    if (obj.sidebarOrder !== undefined) payload.sidebarOrder = obj.sidebarOrder;
    if (obj.sidebarPinned !== undefined) payload.sidebarPinned = obj.sidebarPinned;
    if (obj.homeWidgetsHidden !== undefined) payload.homeWidgetsHidden = obj.homeWidgetsHidden;
    if (obj.homeWidgetsOrder !== undefined) payload.homeWidgetsOrder = obj.homeWidgetsOrder;
    if (obj.kpiSavedViews !== undefined) payload.kpiSavedViews = obj.kpiSavedViews;
    if (obj.recentItems !== undefined) payload.recentItems = obj.recentItems;
    if (obj.onboardingProgress !== undefined) payload.onboardingProgress = obj.onboardingProgress;
    try {
        const response = await axios({
            method: 'patch',
            url: `${config.serverBaseUrl}/v1/web/user/preferences`,
            data: payload,
            headers: {
                'Content-Type': 'application/json',
                ...(obj.accessToken ? { 'Authorization': `Bearer ${obj.accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating preferences:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * Partial update for the Profile page's General settings — display
 * name + optional country / city. Backed by
 * PATCH /v1/web/user/profile (UserProfilePatchDTO on the wire).
 *
 * Any field left undefined is omitted from the payload, so the server's
 * $set-only-non-null behaviour leaves it untouched.
 */
export const updateUserProfile = async ({ accessToken, userName, country, city } = {}) => {
    const payload = {};
    if (userName !== undefined) payload.userName = userName;
    if (country !== undefined) payload.country = country;
    if (city !== undefined) payload.city = city;
    try {
        const response = await axios({
            method: 'patch',
            url: `${config.serverBaseUrl}/v1/web/user/profile`,
            data: payload,
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error updating user profile:', error.response?.data || error.message);
        throw error;
    }
};

export const setPrimaryGoal = async ({ accessToken, goal }) => {
    try {
        const response = await axios({
            method: 'patch',
            url: `${config.serverBaseUrl}/v1/web/user/primary-goal`,
            params: { goal },
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error setting primary goal:', error.response?.data || error.message);
        throw error;
    }
};

/**
 * A/B-test list filters persisted per user + org + game.
 * Backed by GET/PUT /v1/web/user/ab-test-filters. Returns/accepts a free-form
 * map ({ status, creator, dateRange }) so the filter shape can evolve.
 */
export const getAbTestFilters = async ({ accessToken, orgId, gameId } = {}) => {
    try {
        const response = await axios({
            method: 'get',
            url: `${config.serverBaseUrl}/v1/web/user/ab-test-filters`,
            params: { orgId, gameId },
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching AB test filters:', error.response?.data || error.message);
        throw error;
    }
};

export const saveAbTestFilters = async ({ accessToken, orgId, gameId, filters } = {}) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${config.serverBaseUrl}/v1/web/user/ab-test-filters`,
            params: { orgId, gameId },
            data: filters || {},
            headers: {
                'Content-Type': 'application/json',
                ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {})
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error saving AB test filters:', error.response?.data || error.message);
        throw error;
    }
};

export const assignToOrg = async (obj) => {
    try {
        const response = await axios({
            method: 'post',
            url: `${config.serverBaseUrl}/v1/web/user/assign-to-org?orgId=${obj.orgId}`,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${obj.accessToken}`,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Failed to assign to an Org:', error.response?.data || error.message);
        throw error;
    }
};